import MuiIconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import { floatingTooltipSlotProps } from '@/shared/styles/floatingSurface.styles'
import { getIconButtonStyles } from './IconButton.styles'
import styles from './IconButton.module.scss'

export interface IconButtonProps {
  icon: string | React.ReactNode
  onClick: (() => void) | ((event: React.MouseEvent<HTMLElement>) => void)
  color: 'primary' | 'secondary' | 'tertiary'
  variant?: 'text' | 'contained'
  disabled?: boolean
  rotate?: number
  flip?: boolean
  noPadding?: boolean
  /**
   * Accessible name. An icon-only button has no text for a screen reader to
   * announce, so without this it is read as "button" and nothing else. Optional
   * only because every existing caller predates it; new ones should pass it.
   */
  ariaLabel?: string
  /**
   * What the button does, shown on hover and focus and read out as its name
   * unless `ariaLabel` says otherwise. For a row of icons whose meaning is
   * not obvious at a glance.
   */
  tooltip?: string
}

function IconButton(props: IconButtonProps) {
  const {
    icon,
    onClick,
    color,
    variant = 'text',
    disabled = false,
    rotate,
    flip,
    noPadding,
    ariaLabel,
    tooltip
  } = props
  const IconButtonStyles = getIconButtonStyles(color, variant)
  const button = (
    <MuiIconButton
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel ?? tooltip}
      sx={{
        ...IconButtonStyles,
        padding: noPadding
          ? 0
          : variant === 'contained'
            ? '0.375rem'
            : '0.125rem',
        '&:focus': {
          outline: 'none'
        },
        rotate: rotate ? `${rotate}deg` : '0deg',
        transform: flip ? 'scaleX(-1)' : 'none'
      }}
      disableRipple
    >
      {typeof icon === 'string' ? <i className={`${icon}`} /> : icon}
    </MuiIconButton>
  )

  if (!tooltip) return button
  return (
    <Tooltip
      title={<span className="body-bold-medium">{tooltip}</span>}
      placement="top"
      arrow
      slotProps={floatingTooltipSlotProps}
    >
      {/* A disabled button gets no pointer events; the span still does, so
          the tooltip says what a greyed-out icon would do. */}
      <span className={styles.tooltipAnchor}>{button}</span>
    </Tooltip>
  )
}

export default IconButton
